import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, DollarSign, Wallet, FileText, Hourglass, LifeBuoy, Lightbulb, MailWarning, Sparkles, ArrowRight, Inbox } from 'lucide-react';

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const Stat = ({ icon: Icon, label, value, hint, to, testId, danger }) => (
  <Card data-testid={testId} className={danger ? 'border-[hsl(0_72%_40%)]/40' : undefined}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className={`text-2xl font-semibold mt-1 tabular-nums${danger ? ' text-[hsl(0_72%_40%)]' : ''}`}>{value}</p>
          {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
        </div>
        <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
          <Icon className="h-4.5 w-4.5" />
        </div>
      </div>
      {to && (
        <Link to={to} className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`${testId}-link`}>
          View <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </CardContent>
  </Card>
);

export default function OwnerDashboard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get('/owner/dashboard').then((r) => setD(r.data)).catch(() => {});
  }, []);

  if (!d) {
    return (
      <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-4" data-testid="owner-dashboard-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div>
      </div>
    );
  }

  // Name the worst-hit practices so a dead sending key is traceable at a glance.
  const failureHint = (d.email_failures_by_practice || []).length
    ? (d.email_failures_by_practice || []).map((f) => `${f.practice_name} (${f.count})`).join(' · ')
    : 'Undelivered emails, all practices';

  const months = Object.keys(d.monthly_volume || {}).sort().slice(-6);
  const maxV = Math.max(1, ...months.map((m) => d.monthly_volume[m]));

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-6" data-testid="owner-dashboard">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Platform overview</h1>
        <p className="text-sm text-muted-foreground">Your doctor customers, revenue and platform health — owner eyes only.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="owner-dashboard-stats">
        <Stat icon={Building2} label="Practices" value={`${d.practices_active} active`} hint={`${d.practices_total} total · ${d.users_total} users`} to="/owner/practices" testId="owner-stat-practices" />
        <Stat icon={DollarSign} label="Subscription MRR" value={fmt(d.subscription_mrr)} hint={`${d.mrr_practices ?? 0} of ${d.practices_active ?? 0} practices billing · base + seat overages`} testId="owner-stat-mrr" />
        <Stat icon={Wallet} label="Patient fees collected" value={fmt(d.patient_fees_collected)} hint="Per-document fees, all practices" testId="owner-stat-fees" />
        <Stat icon={FileText} label="Submissions" value={d.submissions_total} hint={`${d.submissions_complete} complete · ${d.submissions_awaiting_doctor} awaiting doctor`} testId="owner-stat-submissions" />
        {/* ADR 0001: billable documents from the ledger — drafts never counted. */}
        <Stat
          icon={FileText}
          label={`Billable documents — ${d.documents_period_label || 'this month'}`}
          value={d.documents_month?.submitted ?? 0}
          hint={`${d.documents_month?.in_progress ?? 0} in progress · ${d.documents_month?.completed ?? 0} completed`}
          testId="owner-stat-documents"
        />
        <Stat
          icon={Wallet}
          label="Accruing to practice invoices"
          value={fmt(d.documents_month?.accrued_total)}
          hint={`${d.documents_month?.accrued_count ?? 0} document(s) awaiting month-end rollup`}
          testId="owner-stat-accrued"
        />
        {(d.ledger_errors ?? 0) > 0 && (
          <Stat
            icon={MailWarning}
            label="Documents missing from the ledger"
            value={d.ledger_errors}
            hint="Ledger write failed when the case reached a doctor — bill these by hand"
            danger
            testId="owner-stat-ledger-errors"
          />
        )}
        {(d.paid_after_cancel ?? 0) > 0 && (
          <Stat
            icon={MailWarning}
            label="Refund due (cancelled)"
            value={d.paid_after_cancel}
            hint="A cancelled case holds a patient's payment — refund it in Stripe"
            danger
            testId="owner-stat-paid-after-cancel"
          />
        )}
        {/* ADR 0008: the third rail. Revenue counts only captured holds. */}
        {d.patient_direct && (d.patient_direct.enabled || d.patient_direct.requested + d.patient_direct.accepted + d.patient_direct.expired > 0) && (
          <Stat
            icon={Inbox}
            label="Patient-direct revenue (captured)"
            value={fmt(d.patient_direct.revenue)}
            hint={`${d.patient_direct.accepted} accepted · ${d.patient_direct.requested} open (${d.patient_direct.waiting_on_office} waiting on an office) · ${d.patient_direct.expired} expired · ${d.patient_direct.declined} declined`}
            testId="owner-stat-patient-direct"
          />
        )}
        {(d.patient_direct?.hold_release_errors ?? 0) > 0 && (
          <Stat
            icon={MailWarning}
            label="Card holds not released"
            value={d.patient_direct.hold_release_errors}
            hint="A declined or expired request's hold could not be cancelled — cancel it in Stripe (it lapses on its own in ~7 days)"
            danger
            testId="owner-stat-pd-hold-errors"
          />
        )}
        {/* ADR 0002: what the AI cost US this month. A cost record and a runaway
            detector, never an invoice line — AI is included in the subscription. */}
        <Stat
          icon={Sparkles}
          label={`AI cost (estimate) — ${d.documents_period_label || 'this month'}`}
          value={fmt(d.ai_usage_month?.cost_usd)}
          hint={`${d.ai_usage_month?.calls ?? 0} model calls · fair-use line ${fmt(d.ai_usage_month?.allowance_usd)} · included, never invoiced`}
          testId="owner-stat-ai-cost"
        />
        <Stat icon={LifeBuoy} label="Open support tickets" value={d.open_tickets} to="/owner/support" testId="owner-stat-tickets" />
        <Stat icon={Lightbulb} label="Open feedback" value={d.open_feedback} hint="Feature requests & bug reports" to="/owner/feedback" testId="owner-stat-feedback" />
        <Stat
          icon={MailWarning}
          label={`Email failures (${d.email_failure_window_days ?? 30}d)`}
          value={d.email_failures ?? 0}
          hint={failureHint}
          danger={(d.email_failures ?? 0) > 0}
          testId="owner-stat-email-failures"
        />
        <Stat
          icon={Sparkles}
          label="AI prep stalled"
          value={d.stalled_auto_prep ?? 0}
          hint={`Paid cases unprepped after ${d.auto_prep_stale_minutes ?? 10} min and retries`}
          danger={(d.stalled_auto_prep ?? 0) > 0}
          testId="owner-stat-stalled-auto-prep"
        />
        <Stat
          icon={Hourglass}
          label="Paid, never sent to a doctor"
          value={d.stalled_in_review ?? 0}
          hint={`Waiting with staff over ${Math.round((d.stalled_in_review_hours ?? 72) / 24)} days — a case that never reaches a provider is never billed`}
          danger={(d.stalled_in_review ?? 0) > 0}
          testId="owner-stat-stalled-in-review"
        />
      </div>

      <Card data-testid="owner-volume-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Completed documents — all practices</CardTitle></CardHeader>
        <CardContent>
          {months.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No completed documents yet.</p>
          ) : (
            <div className="flex items-end gap-3 h-36 pt-2">
              {months.map((m) => (
                <div key={m} className="flex-1 h-full flex flex-col items-center justify-end gap-1">
                  <span className="text-xs font-medium tabular-nums">{d.monthly_volume[m]}</span>
                  <div className="w-full max-w-[56px] bg-primary/80 rounded-t" style={{ height: `${(d.monthly_volume[m] / maxV) * 75}%`, minHeight: 6 }} />
                  <span className="text-[10px] text-muted-foreground">{m}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
